import { addAlert } from '../alerts.js';
import { saveWindowState } from '../windows.js';
import { initializeSelect } from '../manuals/form.js';
import {
    sendPostRequest,
    sendPutRequest,
    initializeDependentGroupTypeSelects,
} from '../utils.js';

const globalComponentPopup = {
    config: {
        endpoints: {
            create: '/api/execution/global-components',
            update: (id) => `/api/execution/global-components/${id}`,
            getDetails: (id) => `/api/execution/global-components/${id}`,
        },
    },

    // --- DOM Elements ---
    form: null,
    modalContainer: null,
    submitBtn: null,
    btnText: null,
    btnLoader: null,
    titleElement: null,
    nameInput: null,
    quantityInput: null,
    deviceGroupQuery: null,
    deviceTypeQuery: null,
    _originalData: null,

    init() {
        this.form = document.getElementById('globalComponentWindow');
        if (!this.form) return;

        this._cacheDOMElements();
        this._initializeChoices();
        this._bindEvents();

        const savedStateRaw = localStorage.getItem(
            `windowState-${this.form.id}`,
        );
        if (savedStateRaw) {
            try {
                const savedState = JSON.parse(savedStateRaw);
                if (savedState.open) {
                    this.openInCreateMode();
                }
            } catch (error) {
                console.error('Failed to parse saved window state:', error);
            }
        }
    },

    _cacheDOMElements() {
        this.modalContainer = this.form.closest('.window-modal');
        this.submitBtn = this.form.querySelector('button[type="submit"]');
        this.btnText = this.submitBtn.querySelector('.btn-text');
        this.btnLoader = this.submitBtn.querySelector('.btn-loader');
        this.titleElement = this.form.querySelector('.window-title');
        this.nameInput = this.form.querySelector('#id_name');
        this.quantityInput = this.form.querySelector('#id_target_quantity');
        this.deviceGroupQuery = this.form.querySelector('#id_device_group');
        this.deviceTypeQuery = this.form.querySelector('#id_device_type');
        this.deviceManufacturerQuery = this.form.querySelector(
            '#id_device_manufacturer',
        );
        this.deviceModelsInput = this.form.querySelector('#id_device_models');
    },

    _initializeChoices() {
        initializeDependentGroupTypeSelects(
            this.deviceGroupQuery,
            this.deviceTypeQuery,
        );
        initializeSelect(this.deviceManufacturerQuery);
    },

    _bindEvents() {
        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));

        if (this.nameInput) {
            this.nameInput.addEventListener('input', () => {
                this._validateFields();
                this._updateSubmitButtonState();
            });
        }
        if (this.quantityInput) {
            this.quantityInput.addEventListener('input', () => {
                this._validateFields();
                this._updateSubmitButtonState();
            });
        }
        if (this.deviceModelsInput) {
            this.deviceModelsInput.addEventListener('input', () => {
                this._updateSubmitButtonState();
            });
        }
        if (this.deviceGroupQuery) {
            this.deviceGroupQuery.addEventListener('change', () => {
                this._updateSubmitButtonState();
            });
        }
        if (this.deviceTypeQuery) {
            this.deviceTypeQuery.addEventListener('change', () => {
                this._updateSubmitButtonState();
            });
        }
        if (this.deviceManufacturerQuery) {
            this.deviceManufacturerQuery.addEventListener('change', () => {
                this._updateSubmitButtonState();
            });
        }

        const createBtn = document.querySelector(
            '.window-toggle[data-window="globalComponentWindow"]',
        );
        if (createBtn) {
            createBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.openInCreateMode();
            });
        }

        const closeButtons = this.form.querySelectorAll('.window-close');
        closeButtons.forEach((btn) =>
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.closeAndReset();
            }),
        );
    },

    _validateFields() {
        const validateField = (inputElement) => {
            if (!inputElement) return false;
            let isValid = inputElement.value.trim().length > 0;
            if (inputElement.type == 'number') {
                isValid = isValid && !isNaN(inputElement.value);
            }
            return isValid;
        };

        const isNameValid = validateField(this.nameInput);
        const isQuantityValid = validateField(this.quantityInput);

        return isNameValid && isQuantityValid;
    },

    _updateSubmitButtonState() {
        let isDisabled = !this._validateFields();

        if (!isDisabled && this.form.dataset.mode === 'duplicate') {
            isDisabled = !this._hasChanges();
        }

        this.submitBtn.disabled = isDisabled;
        if (isDisabled) {
            this.submitBtn.classList.add('disabled');
        } else {
            this.submitBtn.classList.remove('disabled');
        }
    },

    openInCreateMode() {
        this.resetModal();
        this._configureForCreate();
        this._updateSubmitButtonState();
        this._showModal();
    },

    async openInEditMode(id) {
        this.resetModal();
        this._configureForEdit(id);

        try {
            const response = await fetch(this.config.endpoints.getDetails(id));
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();

            await this._populateForm(data);
            this._validateFields();
            this._updateSubmitButtonState();
        } catch (error) {
            console.error('Error opening edit mode:', error);
            addAlert(
                'danger',
                gettext('Nie udało się załadować danych do edycji.'),
            );
            return;
        }

        this._showModal();
    },

    async openInDuplicateMode(id) {
        this.resetModal();
        this._configureForDuplicate();

        try {
            const response = await fetch(this.config.endpoints.getDetails(id));
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();

            await this._populateForm(data);
            this._originalData = this._getFormSnapshot();
            this._validateFields();
            this._updateSubmitButtonState();
        } catch (error) {
            console.error('Error opening duplicate mode:', error);
            addAlert(
                'danger',
                gettext('Nie udało się załadować danych do duplikacji.'),
            );
            return;
        }

        this._showModal();
    },

    async handleFormSubmit(event) {
        event.preventDefault();
        this._setLoadingState(true);

        const mode = this.form.dataset.mode;
        const id = this.form.dataset.id;
        const payload = this._buildPayload();
        console.log('payload', payload, JSON.stringify(payload));

        let isSuccess = false;

        try {
            if (mode === 'create' || mode === 'duplicate') {
                await sendPutRequest(this.config.endpoints.create, payload);
                addAlert('success', gettext('Nowy element został utworzony.'));
            } else {
                await sendPostRequest(
                    this.config.endpoints.update(id),
                    payload,
                );
                addAlert('success', gettext('Element został zaktualizowany.'));
            }
            isSuccess = true;
        } catch (error) {
            console.error(`Error during ${mode}:`, error);
            addAlert(
                'error',
                error.message || gettext('Wystąpił nieoczekiwany błąd.'),
            );
        } finally {
            if (isSuccess) {
                this._notifyUpdated();
                this.closeAndReset();
            } else {
                this._setLoadingState(false);
            }
        }
    },

    _buildPayload() {
        const formData = new FormData(this.form);
        const payload = Object.fromEntries(formData.entries());

        if (payload.target_quantity)
            payload.target_quantity = parseInt(payload.target_quantity);

        if (payload.device_group !== '') {
            payload.device_group_id = parseInt(payload.device_group);
        } else {
            payload.device_group_id = null;
        }
        if (payload.device_type !== '') {
            payload.device_type_id = parseInt(payload.device_type);
        } else {
            payload.device_type_id = null;
        }
        if (payload.device_manufacturer !== '') {
            payload.device_manufacturer_id = parseInt(
                payload.device_manufacturer,
            );
        } else {
            payload.device_manufacturer_id = null;
        }

        delete payload.search_terms;
        delete payload.device_group;
        delete payload.device_type;
        delete payload.device_manufacturer;

        return payload;
    },

    _notifyUpdated() {
        document.dispatchEvent(new Event('globalComponentUpdated'));
    },

    _setLoadingState(isLoading) {
        this.btnText.style.display = isLoading ? 'none' : 'inline';
        this.btnLoader.style.display = isLoading ? 'inline' : 'none';
        this.submitBtn.disabled = isLoading;
        if (isLoading) {
            this.submitBtn.classList.add('disabled');
        } else {
            // Re-check validation state to decide if we should enable
            this._updateSubmitButtonState();
        }
    },

    _configureForCreate() {
        this.form.dataset.mode = 'create';
        this.titleElement.textContent = gettext('DODAJ NOWY ELEMENT');
        this.btnText.textContent = gettext('ZAPISZ');
        this._clearChoices();
    },

    _configureForEdit(id) {
        this.form.dataset.mode = 'edit';
        this.form.dataset.id = id;
        this.titleElement.textContent = gettext('EDYCJA ELEMENTU');
        this.btnText.textContent = gettext('ZAPISZ ZMIANY');
        this._clearChoices();
    },

    _configureForDuplicate() {
        this.form.dataset.mode = 'duplicate';
        this.form.dataset.id = '';
        this.titleElement.textContent = gettext('DUPLIKUJ ELEMENT');
        this.btnText.textContent = gettext('ZAPISZ');
        this._clearChoices();
    },

    async _populateForm(data) {
        if (this.nameInput) this.nameInput.value = data.name;
        if (this.quantityInput) this.quantityInput.value = data.target_quantity;
        if (this.deviceModelsInput)
            this.deviceModelsInput.value = data.device_models || '';

        this.deviceGroupQuery._choices.setChoiceByValue(
            String(data.device_group || ''),
        );
        // enforce set of propper choices for deviceTypeQuery
        this.deviceGroupQuery.dispatchEvent(new Event('change'));
        await new Promise((resolve) => setTimeout(resolve, 100));

        this.deviceTypeQuery._choices.setChoiceByValue(
            String(data.device_type || ''),
        );
        this.deviceManufacturerQuery._choices.setChoiceByValue(
            String(data.device_manufacturer || ''),
        );
    },

    _getFormSnapshot() {
        return {
            name: this.nameInput ? this.nameInput.value : '',
            device_group: this.deviceGroupQuery
                ? this.deviceGroupQuery.value
                : '',
            device_type: this.deviceTypeQuery ? this.deviceTypeQuery.value : '',
            device_manufacturer: this.deviceManufacturerQuery
                ? this.deviceManufacturerQuery.value
                : '',
            device_models: this.deviceModelsInput
                ? this.deviceModelsInput.value
                : '',
        };
    },

    _hasChanges() {
        if (!this._originalData) return true;
        const current = this._getFormSnapshot();
        return Object.keys(this._originalData).some(
            (key) => String(current[key]) !== String(this._originalData[key]),
        );
    },

    _clearChoices() {
        if (this.deviceGroupQuery && this.deviceGroupQuery._choices) {
            this.deviceGroupQuery._choices.setChoiceByValue('');
        }
        if (this.deviceTypeQuery && this.deviceTypeQuery._choices) {
            this.deviceTypeQuery._choices.setChoiceByValue('');
        }
        if (
            this.deviceManufacturerQuery &&
            this.deviceManufacturerQuery._choices
        ) {
            this.deviceManufacturerQuery._choices.setChoiceByValue('');
        }
    },

    _showModal() {
        this.modalContainer.classList.add('window-modal-open');
        this.form.classList.add('window-open');
    },

    closeAndReset() {
        this.modalContainer.classList.remove('window-modal-open');
        this.form.classList.remove('window-open');
        saveWindowState(this.form);
        this.resetModal();
        this._setLoadingState(false);
    },

    resetModal() {
        this._originalData = null;
        this.form.dataset.mode = '';
        this.form.dataset.id = '';
        this.nameInput.value = '';
        this.quantityInput.value = '';
        if (this.form.querySelectorAll('.is-valid').length > 0) {
            this.form.querySelectorAll('.is-valid').forEach((el) => {
                el.classList.remove('is-valid');
            });
        }
        this._clearChoices();
    },
};

window.globalComponentPopup = globalComponentPopup;

export function initializeCreateGlobalComponentPopup() {
    window.globalComponentPopup.init();
}
